import {Grid, Box} from '@mui/material'
import React, {useEffect, useState, useMemo} from 'react'
import {useDispatch, useSelector, batch} from 'react-redux'
import {LoopOutlined as LoopOutlinedIcon} from '@mui/icons-material'
import {addDays, startOfMonth} from 'date-fns'
import {RootStore} from '../../../redux/store'
import Tab, {TabDetails} from '../../../shared/UI/Tab'
import FindPerson from './FindPerson'
import {BookingFloorPlan, BookingLocation, BookingZone} from '../../../services/booking/types'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import Button from '../../../shared/UI/Button'
import Paragraph from '../../../shared/UI/Paragraph'
import LoadingIndicator from '../../../shared/UI/LoadingIndicator'
import actionsService from '../../../services/actionsService'
import {
  setBookingFloorplanViewingDate,
  setDeskBookingFocussedZoneID,
  setDeskBookingLoading,
  setDeskBookingOpenFloorplan,
  clearFoundDesk,
  setDeskBookingShowGridView,
  setDeskBookingShowFloorplan,
  setDeskBookingFloorplanManuallySelected,
  setBookingGridViewingDate,
  setDeskBookingDashboardResults,
} from '../../../redux/reducers/deskBookingReducer'
import {showErrorMessage} from '../../../redux/reducers/snackbarReducer'
import BookingErrorMessage from '../utils/BookingErrorMessage'
import {getJson} from '../utils/utils'
import {FIRST_FLOORPLAN_IN_ARRAY} from '../consts'
import {BookingSearchType, Time, BookingSliderIndex} from './enums'
import {BookingWizardSteps} from '../BlockBooking/enums'
import {BookinResetSearch} from './components'
import {setBookingSliderPosition} from '../../../redux/reducers/deskBookingReducer'
import {showComponents} from '../utils'
import {
  setDeskBookingSearchParams,
  setDeskBookingSearchResults,
} from '../../../redux/reducers/deskBookingSearchReducer'
import {
  handleSearch,
  getDashboardBookings,
  onBookingGridViewDataLoaded,
} from '../bookingLogic'
import {checkIsOpenFloorplanArea} from './bookingFloorplanLogic'


interface FormFieldsSelectedCheck {
  isLocationSelected: boolean
  isFloorplanSelected: boolean
  isZoneSelected: boolean
}

export default function BookingSearch() {

  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())
  const [selectedLocation, setSelectedLocation] = useState<BookingLocation>()
  const [selectedFloorplan, setSelectedFloorplan] = useState<BookingFloorPlan>()
  const [selectedZone, setSelectedZone] = useState<BookingZone>()

  const [isOpenFloorplanArea, setIsOpenFloorplanArea] = useState<boolean>(false)

  const dispatch = useDispatch()

  const handleSearchError = (err: string) => {
    dispatch(showErrorMessage(<BookingErrorMessage name={err} />))
  }

  const {
    locations,
    floorplans,
    zones,
    featuresForFloorPlan,
    showGridView,
    bookingIsLoading,
    focussedZoneID,
  } = useSelector((state: RootStore) => state.deskBooking)
  const {searchResults, searchParams} = useSelector(
    (state: RootStore) => state.deskBookingSearch
  )

  const {currentStep: wizardCurrentStep, weekdaysSelected: wizardWeekdaysSelected} = useSelector(
    (state: RootStore) => state.deskBookingWizard
  )

  const {employeeDetails} = useSelector((state: RootStore) => state.appSettings)
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)

  const searchType: BookingSearchType = BookingSearchType.SINGLE

  const zonesForDropdown = () => {
    if (!zones || !selectedFloorplan) {
      return []
    }
    return zones
      .filter(f => f.floorPlanId === selectedFloorplan?.id)
      .filter(m => {
        const json = getJson(m.additionalInfo)
        return !json?.ui || json?.ui?.showInDropdown === true
      })
  }

  const employeeFloorplans =
    useMemo(() =>
        floorplans.filter(f =>
          f.locationId === employeeDetails.location.id) ||
        floorplans[FIRST_FLOORPLAN_IN_ARRAY]
      , [employeeDetails.location.id, floorplans])

  const employeeZones =
    useMemo(() =>
        zones.filter(z =>
          employeeFloorplans.some(s => s.id === z.floorPlanId) &&
          z.belongsTo === employeeDetails.departmentId
        )
      , [employeeDetails.departmentId, employeeFloorplans, zones])


  const [formFieldsSelectedCheck, setFormFieldsSelectedCheck] = useState<FormFieldsSelectedCheck>({
    isLocationSelected: false,
    isFloorplanSelected: false,
    isZoneSelected: false,
  })

  useEffect(() => {

    const isLocationSelected = selectedLocation !== undefined && selectedLocation !== null
    const isFloorplanSelected = selectedFloorplan !== undefined && selectedFloorplan !== null
    const isZoneSelected = selectedZone !== undefined && selectedZone !== null

    setFormFieldsSelectedCheck({
      isLocationSelected,
      isFloorplanSelected,
      isZoneSelected
    })

  }, [selectedLocation, selectedFloorplan, selectedZone])

  const resetFilterFloorplan = () => {
    batch(() => {
      dispatch(setBookingGridViewingDate(startOfMonth(new Date())))
      dispatch(setDeskBookingFloorplanManuallySelected(false))
      dispatch(setDeskBookingShowGridView(true))
      dispatch(setDeskBookingShowFloorplan(false))

      showComponents(dispatch, { gridview: true, floorplan: false, navbar: true, search: true, filter: false })
      
      dispatch(setBookingSliderPosition({
        slidePosition: BookingSliderIndex.GRID_VIEW,
        buttonClick: false
      }))
      
      dispatch(setDeskBookingSearchParams(undefined))
      dispatch(setDeskBookingSearchResults([]))
      
      dispatch(setDeskBookingDashboardResults([]))
      dispatch(setDeskBookingLoading(true))
    })
    
    const singleDayBookingQuery = false
    getDashboardBookings(
      employeeDetails.employeeId,
      new Date(),
      dispatch,
      onBookingGridViewDataLoaded,
      singleDayBookingQuery
    )
  }

  useEffect(() => {

    setSelectedLocation(locations.find(f => f.id === employeeDetails.location.id))
  }, [locations, employeeDetails.location.id])

  useEffect(() => {

    if (!searchParams || !zones || !searchResults || !employeeDetails) {
      return
    }

    const populateSearchBoxes = (floorplan: BookingFloorPlan) => batch(() => {

      setSelectedDate(searchParams.date || new Date())
      setSelectedLocation(locations.find(f => f.id === employeeDetails.location.id))
      if (searchParams?.locationId) {
        setSelectedLocation(locations.find(f => f.id === searchParams?.locationId))
      }

      setSelectedFloorplan(floorplan)
      if (searchParams.zoneId) {
        setSelectedZone(zones.find(f => f.id === searchParams.zoneId))
      }
      if (!searchParams.zoneId && focussedZoneID) {
        setSelectedZone(zones.find(f => f.id === focussedZoneID))
      }

    })

    const floorplan = floorplans.find(f => f.id === searchParams?.floorplanId)
    if (!floorplan) {
      return
    }

    populateSearchBoxes(floorplan)

  }, [dispatch, searchParams, zones && floorplans && locations, focussedZoneID])

  const employeeLocationId = locations.find(l => l.id === employeeDetails.location.id)?.id

  useEffect(() => {

    const thisSelectedLocationId = selectedLocation?.id || employeeLocationId || 0
    const thisSelectedFloorplanId = selectedFloorplan?.id || 0

    setIsOpenFloorplanArea(
      checkIsOpenFloorplanArea(thisSelectedLocationId, thisSelectedFloorplanId)
    )

  }, [selectedLocation && selectedFloorplan])

  const disableFind = () => {

    if (wizardCurrentStep !== BookingWizardSteps.STEP_0_INACTIVE &&
      wizardWeekdaysSelected.length === 0
    ) {
      return true
    }

    const {
      isFloorplanSelected,
      isLocationSelected,
      isZoneSelected
    } = formFieldsSelectedCheck

    const isOpenFloorplanOrZone = isOpenFloorplanArea ? true : isZoneSelected

    return !(isLocationSelected && isFloorplanSelected && isOpenFloorplanOrZone)
  }

  const [showFilterLoading, setShowFilterLoading] = useState(false)

  const filterDataNotAvailable = !locations?.length || !floorplans?.length || !zones?.length

  useEffect(() => {

    if (!filterDataNotAvailable) {

      return
    }

    const showLoadingTimeId = setTimeout(() => {

      setShowFilterLoading(true)
    }, 3000)

    return () => {
      clearTimeout(showLoadingTimeId)
    }
  }, [])

  if (filterDataNotAvailable) {

    if (!showFilterLoading) {
      return null
    }

    return (
      <Box display="flex" height="470px" flexDirection="column" justifyContent="top">
        <Box display="flex" alignItems="center" flexDirection="column">
          <LoadingIndicator show alignItems="center" margin="20px" />
          <Paragraph size="16px" weight="bold" color="#2c2965">
            Loading filter data...
          </Paragraph>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} mb={1}>
          <Tab
            titleAndContent={
              [
                {
                  label: 'Filters',
                  tabContext: <></>,
                },
                {
                  label: 'Employee search',
                  tabContext: <></>,
                },
              ] as TabDetails[]
            }
            tabChange={selectedTab}
            onChange={newTab => {
              setSelectedTab(newTab)
              // Clear found desk highlight when switching tabs
              dispatch(clearFoundDesk())

              // Track analytics for tab clicks
              const eventType =
                newTab === 0 ? 'BookingFilterTabClick' : 'BookingEmployeeSearchTabClick'
              actionsService.userAnalytics
                .postEvent('UIClickEvent', eventType, window.location.pathname, window.location.hostname)
                .catch((error: unknown) => console.error('Analytics tracking error:', error))
            }}
            removeInactiveArrows
            sx={{
              '& .MuiTabs-flexContainer': {
                gap: '8px',
              },
              '& .MuiTab-root': {
                fontWeight: 400,
                fontSize: '13px',
                letterSpacing: '0.2px',
                textTransform: 'none',
                padding: '10px 0',
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
            }}
          />
        </Grid>
      </Grid>
      {selectedTab === 1 && <FindPerson mode="filter" />}
      {selectedTab === 0 && (
        <>
          <Grid container spacing={2} height="540px" alignContent="flex-start" mt={2}>
            <Grid item xs={12}>
              <DropdownMenu
                label="Location"
                data={locations}
                textField="name"
                valueField="id"
                id="location"
                value={selectedLocation?.id || employeeLocationId || ''}
                onChange={e => {
                  if (selectedLocation?.id === Number(e.target.value)) {
                    return
                  }

                  setSelectedLocation(
                    locations.find(f => f.id === (Number(e.target.value) || employeeLocationId))
                  )

                  setSelectedFloorplan(undefined)
                  setSelectedZone(undefined)
                }}
                onOpen={() => {
                  actionsService.userAnalytics
                    .postEvent(
                      'UIClickEvent',
                      'FilterLocationDropdownClick',
                      window.location.pathname,
                      window.location.hostname
                    )
                    .catch((error: unknown) => console.error('Analytics tracking error:', error))
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <DropdownMenu
                label="* Floor"
                data={floorplans.filter(
                  f => f.locationId === (selectedLocation?.id || employeeLocationId)
                )}
                textField="name"
                valueField="id"
                id="floorplan"
                value={selectedFloorplan?.id || ''}
                onChange={e => {
                  const thisSelectedFloorPlanId = Number(e.target.value)
                  if (selectedFloorplan?.id === thisSelectedFloorPlanId) {
                    return
                  }

                  setSelectedFloorplan(floorplans.find(f => f.id === thisSelectedFloorPlanId))

                  const thisSelectedLocationId = selectedLocation?.id || employeeLocationId || 0

                  setIsOpenFloorplanArea(
                    checkIsOpenFloorplanArea(thisSelectedLocationId, thisSelectedFloorPlanId)
                  )

                  setSelectedZone(undefined)
                }}
                onOpen={() => {
                  actionsService.userAnalytics
                    .postEvent(
                      'UIClickEvent',
                      'FilterFloorDropdownClick',
                      window.location.pathname,
                      window.location.hostname
                    )
                    .catch((error: unknown) => console.error('Analytics tracking error:', error))
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <DropdownMenu
                label={`${isOpenFloorplanArea ? '' : '* '}Zone`}
                data={isOpenFloorplanArea ? [{ id: 0, name: 'Any' }] : zonesForDropdown()}
                textField="name"
                valueField="id"
                disabled={!selectedFloorplan || isOpenFloorplanArea}
                id="zone"
                value={isOpenFloorplanArea ? 0 : selectedZone?.id || ''}
                onChange={e => {
                  if (selectedZone?.id === Number(e.target.value)) {
                    return
                  }

                  const zone = zones.find(f => f.id === Number(e.target.value))
                  setSelectedZone(zone)
                }}
                onOpen={() => {
                  actionsService.userAnalytics
                    .postEvent(
                      'UIClickEvent',
                      'FilterZoneDropdownClick',
                      window.location.pathname,
                      window.location.hostname
                    )
                    .catch((error: unknown) => console.error('Analytics tracking error:', error))
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              zIndex: 1000,
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                label="Apply"
                disabled={disableFind()}
                loading={!showGridView && bookingIsLoading}
                fullWidth
                style={{ margin: 0 }}
                onClick={e =>
                  batch(async () => {
                    // Track analytics
                    actionsService.userAnalytics
                      .postEvent(
                        'UIClickEvent',
                        'FilterApplyButtonClick',
                        window.location.pathname,
                        window.location.hostname
                      )
                      .catch((error: unknown) => console.error('Analytics tracking error:', error))

                    if (!selectedLocation || !selectedFloorplan) {
                      return
                    }

                    const selectedData = {
                      selectedFromTo: {
                        from: Time.MIDNIGHT,
                        to: Time.MIDNIGHT_MINUS_ONE_SECOND,
                      },
                      searchType,
                    }

                    dispatch(
                      setDeskBookingSearchParams({
                        date: searchParams?.date ?? new Date(),
                        from: Time.MIDNIGHT,
                        to: Time.MIDNIGHT_MINUS_ONE_SECOND,
                        autoSearch: searchParams?.autoSearch ?? false,
                        locationId: selectedLocation?.id,
                        floorplanId: selectedFloorplan?.id,
                        zoneId: selectedZone?.id,
                      })
                    )

                    dispatch(setDeskBookingFocussedZoneID(selectedZone?.id || 0))

                    if (wizardCurrentStep === BookingWizardSteps.STEP_0_INACTIVE) {
                      if (!selectedDate) {
                        return
                      }

                      dispatch(setBookingFloorplanViewingDate(selectedDate))

                      if (!selectedLocation || !selectedFloorplan) {
                        return
                      }

                      dispatch(setDeskBookingOpenFloorplan(isOpenFloorplanArea))

                      const singleQuery = true
                      await handleSearch(
                        dispatch,
                        handleSearchError,
                        singleQuery,
                        zones,
                        featuresForFloorPlan,
                        employeeDetails,
                        userInfo,
                        selectedData,
                        selectedLocation.id,
                        selectedFloorplan.id,
                        null,
                        selectedDate,
                        addDays(selectedDate, 1)
                      )
                    }
                  })
                }
              />

              <BookinResetSearch
                id="reset"
                onClick={() => {
                  // Track analytics
                  actionsService.userAnalytics
                    .postEvent(
                      'UIClickEvent',
                      'FilterResetButtonClick',
                      window.location.pathname,
                      window.location.hostname
                    )
                    .catch((error: unknown) => console.error('Analytics tracking error:', error))

                  resetFilterFloorplan()
                }}
              >
                <LoopOutlinedIcon style={{ width: '18px', marginRight: '5px' }} />
                Reset
              </BookinResetSearch>
            </Grid>
          </Grid>
        </>
      )}
    </>
  )
}
